import { useState, useMemo, useRef, useEffect } from "react";
import { X, Info, GraduationCap, UserCheck, Calendar, Search, Check, ChevronDown, UserMinus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function SchoolClassModal({
  isOpen,
  onClose,
  onSave,
  schoolClass,
  formData,
  setFormData,
  employees = [],
  schoolYears = [], // New prop added to accept the list of school years
}) {
  const [empSearchQuery, setEmpSearchQuery] = useState("");
  const [yearSearchQuery, setYearSearchQuery] = useState("");
  
  const [isEmpDropdownOpen, setIsEmpDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  
  const empDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);

  // Auto-close dropdown menus on outer clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (empDropdownRef.current && !empDropdownRef.current.contains(event.target)) {
        setIsEmpDropdownOpen(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setIsYearDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getEmployeeDisplayName = (emp) => {
    if (!emp) return "";
    if (emp.full_name) return emp.full_name;
    return `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || `Employee #${emp.id}`;
  };

  // --- Employee Filter Logic ---
  const safeEmployees = Array.isArray(employees) 
    ? employees 
    : (employees?.results && Array.isArray(employees.results)) 
      ? employees.results 
      : [];

  const selectedEmployee = useMemo(() => {
    return safeEmployees.find((emp) => emp.id === formData.employee) || formData.employee_detail;
  }, [formData.employee, formData.employee_detail, safeEmployees]);

  const filteredEmployees = useMemo(() => {
    if (!empSearchQuery) return safeEmployees;
    const cleanQuery = empSearchQuery.toLowerCase().trim();
    return safeEmployees.filter((emp) => {
      const displayName = getEmployeeDisplayName(emp);
      return displayName.toLowerCase().includes(cleanQuery);
    });
  }, [empSearchQuery, safeEmployees]);

  // --- School Year Filter Logic ---
  const safeSchoolYears = Array.isArray(schoolYears) 
    ? schoolYears 
    : (schoolYears?.results && Array.isArray(schoolYears.results)) 
      ? schoolYears.results 
      : [];

  // 🌟 Updated to fall back to year_detail from serializer if raw ID array match fails initially
  const selectedSchoolYear = useMemo(() => {
    return safeSchoolYears.find((yr) => yr.id === formData.year) || formData.year_detail;
  }, [formData.year, formData.year_detail, safeSchoolYears]);

  const filteredSchoolYears = useMemo(() => {
    if (!yearSearchQuery) return safeSchoolYears;
    const cleanQuery = yearSearchQuery.toLowerCase().trim();
    return safeSchoolYears.filter((yr) => {
      const yearName = String(yr.name || yr.year || `Academic Year #${yr.id}`);
      return yearName.toLowerCase().includes(cleanQuery);
    });
  }, [yearSearchQuery, safeSchoolYears]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
          />

          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="bg-white rounded-xl w-full max-w-md shadow-2xl border border-slate-100 relative z-10 my-auto flex flex-col overflow-visible"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />
            
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                  <GraduationCap size={16} className="text-[#002855]" />
                  {schoolClass ? "Modify Class Parameters" : "Provision New School Class"}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Establish structural roster allocations linked to operational rooms.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors cursor-pointer"
              >
                <X size={15} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5 text-xs font-medium text-slate-600 overflow-visible">
              
              {/* Class Name Input Block */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  Class Roster Identifier <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Grade 10-A, Roster Calculus II"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] focus:bg-white transition-all"
                  required
                />
              </div>

              {/* Searchable Academic School Year Selection Dropdown */}
              <div className="space-y-1.5 relative" ref={yearDropdownRef}>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Calendar size={12} className="text-slate-400" />
                  Academic Calendar Term (School Year) <span className="text-rose-500">*</span>
                </label>
                
                <button
                  type="button"
                  onClick={() => {
                    setIsYearDropdownOpen(!isYearDropdownOpen);
                    setYearSearchQuery("");
                    setIsEmpDropdownOpen(false);
                  }}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 text-left outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] hover:bg-white transition-all flex items-center justify-between cursor-pointer shadow-xs"
                >
                  <span className={selectedSchoolYear ? "text-slate-800 font-medium" : "text-slate-400 font-normal"}>
                    {selectedSchoolYear ? (selectedSchoolYear.name || selectedSchoolYear.year) : "Select applicable school year..."}
                  </span>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isYearDropdownOpen ? "rotate-180 text-slate-600" : ""}`} />
                </button>

                <AnimatePresence>
                  {isYearDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 4, scale: 0.99 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.99 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1.5 space-y-1.5 max-h-52 flex flex-col"
                    >
                      <div className="relative flex items-center shrink-0 mx-0.5 mt-0.5">
                        <Search size={13} className="absolute left-2.5 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search school years..."
                          value={yearSearchQuery}
                          onChange={(e) => setYearSearchQuery(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 placeholder-slate-400 outline-none focus:bg-white focus:border-[#002855]/40 transition-all"
                        />
                      </div>

                      <div className="overflow-y-auto flex-1 pr-0.5 space-y-0.5">
                        {filteredSchoolYears.length > 0 ? (
                          filteredSchoolYears.map((yr) => {
                            // 🌟 Checks against either the write-key ID or data-object payload ID
                            const isChosen = formData.year === yr.id || formData.year_detail?.id === yr.id;
                            return (
                              <button
                                key={yr.id}
                                type="button"
                                onClick={() => {
                                  setFormData({ 
                                    ...formData, 
                                    year: yr.id,
                                    year_detail: yr // Updates read matrix context cleanly
                                  });
                                  setIsYearDropdownOpen(false);
                                }}
                                className={`w-full text-left px-2.5 py-1.5 rounded-lg font-medium transition-colors flex items-center justify-between cursor-pointer ${
                                  isChosen 
                                    ? "bg-[#002855]/5 text-[#002855] font-semibold" 
                                    : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                                }`}
                              >
                                <span>{yr.name || yr.year}</span>
                                {isChosen && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                              </button>
                            );
                          })
                        ) : (
                          <div className="text-center py-4 text-[11px] text-slate-400 font-normal">
                            No matching academic years found.
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Searchable Assigned Faculty Member Selection Dropdown */}
              <div className="space-y-1.5 relative" ref={empDropdownRef}>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <UserCheck size={12} className="text-slate-400" />
                  Assigned Faculty Member (Employee)
                </label>
                
                <button
                  type="button"
                  onClick={() => {
                    setIsEmpDropdownOpen(!isEmpDropdownOpen);
                    setEmpSearchQuery("");
                    setIsYearDropdownOpen(false);
                  }}
                  className="w-full px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl font-medium text-slate-800 text-left outline-none focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] hover:bg-white transition-all flex items-center justify-between cursor-pointer shadow-xs"
                >
                  <span className={selectedEmployee ? "text-slate-800 font-medium" : "text-slate-400 font-normal"}>
                    {selectedEmployee ? getEmployeeDisplayName(selectedEmployee) : "Select an assigned employee..."}
                  </span>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isEmpDropdownOpen ? "rotate-180 text-slate-600" : ""}`} />
                </button>

                <AnimatePresence>
                  {isEmpDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 4, scale: 0.99 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.99 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1.5 space-y-1.5 max-h-52 flex flex-col"
                    >
                      <div className="relative flex items-center shrink-0 mx-0.5 mt-0.5">
                        <Search size={13} className="absolute left-2.5 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search ledger names..."
                          value={empSearchQuery}
                          onChange={(e) => setEmpSearchQuery(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 placeholder-slate-400 outline-none focus:bg-white focus:border-[#002855]/40 transition-all"
                        />
                      </div>

                      <div className="overflow-y-auto flex-1 pr-0.5 space-y-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, employee: null, employee_detail: null });
                            setIsEmpDropdownOpen(false);
                          }}
                          className="w-full text-left px-2.5 py-1.5 text-slate-400 font-normal hover:bg-slate-50 hover:text-slate-600 rounded-lg transition-colors flex items-center justify-between cursor-pointer"
                        >
                          <span className="flex items-center gap-1.5"><UserMinus size={12} /> Clear Allocation</span>
                          {!formData.employee && !formData.employee_detail && <Check size={12} className="text-[#002855]" />}
                        </button>

                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((emp) => {
                            const isChosen = formData.employee === emp.id || formData.employee_detail?.id === emp.id;
                            return (
                              <button
                                key={emp.id}
                                type="button"
                                onClick={() => {
                                  setFormData({ 
                                    ...formData, 
                                    employee: emp.id,
                                    employee_detail: emp 
                                  });
                                  setIsEmpDropdownOpen(false);
                                }}
                                className={`w-full text-left px-2.5 py-1.5 rounded-lg font-medium transition-colors flex items-center justify-between cursor-pointer ${
                                  isChosen 
                                    ? "bg-[#002855]/5 text-[#002855] font-semibold" 
                                    : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                                }`}
                              >
                                <span>{getEmployeeDisplayName(emp)}</span>
                                {isChosen && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                              </button>
                            );
                          })
                        ) : (
                          <div className="text-center py-4 text-[11px] text-slate-400 font-normal">
                            No matching employee files found.
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Sync Actions Row / Buttons Footer Block */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-6">
                <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
                  <Info size={12} className="text-slate-300 shrink-0" />
                  Synchronizes ledger instantly
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 bg-white border border-slate-200 rounded-xl transition-all cursor-pointer"
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-xs font-medium text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer active:scale-98"
                  >
                    {schoolClass ? "Update Change" : "Create Classroom"}
                  </button>
                </div>
              </div>

            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}